import ZebraBrowserPrintWrapper from 'zebra-browser-print-wrapper';
import {
  usePrintPrintLabelStockLocationMutation,
  usePrintPrintLabelStockLotMutation,
  usePrintPrintLabelPrinterMutation,
  usePrintPrintLabelShipmentLineMutation,
  usePrintPrintLabelShipmentMutation,
  usePrintStockDocumentMutation,
} from 'store/slices/apiV1/inventory';
import { useDefaultLabelTemplates } from 'hooks/useDefaultLabelTemplates';
import useLocalStorage from 'hooks/useLocalStorage';
import {
  warehouseStorageName,
  warehouseDefaults,
} from 'types/warehouseSettings';
import {
  PrintLabelRequest,
  LabelFormat,
  StockDocument,
  PrintStockDocumentRequest,
  LabelTemplate,
  Printer,
} from 'types/inventory';
import { usePrintPrintLabelUserMutation } from 'store/slices/apiV1/user';
import useSnackbar from 'hooks/useSnackbar';
import { usePrintKitRequestLabelMutation } from 'store/slices/clientV2/kitRequests';
import { usePrintStockDocumentRelatedObjectMutation } from 'store/slices/clientV2/stockDocuments';
import { StockDocumentRelatedObjectSchema } from 'types/clientV2/stockDocuments';
import { pluralizeNoun, snakeCase } from 'utils/functions';
import { ZebraPrinterDevice } from 'views/warehouse/settings';
import { Device } from 'zebra-browser-print-wrapper/lib/types';
import { useCallback, useEffect, useState } from 'react';
import { generateZpl } from 'utils/labels';

type PrintRecordType =
  | 'stockLocation'
  | 'stockLot'
  | 'printer'
  | 'shipmentLine'
  | 'shipment'
  | 'user'
  | 'kitRequest';

export default function usePrinting({
  shipToId,
  recordOrgId,
}: {
  shipToId?: string | null;
  recordOrgId?: string | null;
}) {
  const { dispatchSuccessSnackbar, dispatchErrorSnackbar } = useSnackbar();

  const [
    printPrintStockLocationLabel,
    { isLoading: isLoadingPrintStockLocationLabel },
  ] = usePrintPrintLabelStockLocationMutation();

  const [printPrintStockLotLabel, { isLoading: isLoadingPrintStockLotLabel }] =
    usePrintPrintLabelStockLotMutation();

  const [printPrintPrinterLabel, { isLoading: isLoadingPrintPrinterLabel }] =
    usePrintPrintLabelPrinterMutation();

  const [
    printPrintShipmentLineLabel,
    { isLoading: isLoadingPrintShipmentLineLabel },
  ] = usePrintPrintLabelShipmentLineMutation();

  const [printPrintShipmentLabel, { isLoading: isLoadingPrintShipmentLabel }] =
    usePrintPrintLabelShipmentMutation();

  const [
    printPrintKitRequestLabel,
    { isLoading: isLoadingPrintKitRequestLabel },
  ] = usePrintKitRequestLabelMutation();

  const [printPrintUserLabel, { isLoading: isLoadingPrintUserLabel }] =
    usePrintPrintLabelUserMutation();

  const [printStockDocument, { isLoading: isLoadingPrintStockDocument }] =
    usePrintStockDocumentMutation();

  const [
    printStockDocumentRelatedObject,
    { isLoading: isLoadingPrintStockDocumentRelatedObject },
  ] = usePrintStockDocumentRelatedObjectMutation();

  const { fetching: isFetchingLabelTemplates, getLabelTemplate } =
    useDefaultLabelTemplates({
      shipToId,
      recordOrgId,
    });

  const [settings] = useLocalStorage(warehouseStorageName, warehouseDefaults);

  const [availableZebraPrinters, setAvailableZebraPrinters] = useState<
    ZebraPrinterDevice[]
  >([]);

  // handle zebra browser print
  const handleGetAvailablePrinters = async () => {
    const zebraBrowserPrint = new ZebraBrowserPrintWrapper();
    const availablePrinters = await zebraBrowserPrint.getAvailablePrinters();
    if (Array.isArray(availablePrinters)) {
      setAvailableZebraPrinters(availablePrinters as ZebraPrinterDevice[]);
    } else {
      setAvailableZebraPrinters([] as ZebraPrinterDevice[]);
    }
  };

  useEffect(() => {
    if (settings.useBrowserPrint) {
      handleGetAvailablePrinters();
    }
  }, [settings.useBrowserPrint]);

  const handlePrintStockDocument = useCallback(
    async ({ stockDocumentId }: { stockDocumentId: StockDocument['id'] }) => {
      const printer = settings.printer8511;
      if (!printer) {
        dispatchErrorSnackbar('No printer configured');
        return;
      }

      const payload = {
        printer,
      } as PrintStockDocumentRequest;

      try {
        await printStockDocument({
          id: stockDocumentId,
          payload,
        }).unwrap();

        dispatchSuccessSnackbar('Document printed');
      } catch (err) {
        dispatchErrorSnackbar('Document printing error');
      }
    },
    [
      dispatchSuccessSnackbar,
      dispatchErrorSnackbar,
      printStockDocument,
      settings.printer8511,
    ]
  );

  const handlePrintStockDocumentRelatedObjects = useCallback(
    async ({
      stockDocumentRelatedObjectIds,
    }: {
      stockDocumentRelatedObjectIds: StockDocumentRelatedObjectSchema['id'][];
    }) => {
      const printer = settings.printer8511;
      if (!printer) {
        dispatchErrorSnackbar('No printer configured');
        return;
      }

      try {
        await printStockDocumentRelatedObject({
          ids: stockDocumentRelatedObjectIds,
          printer,
        }).unwrap();

        dispatchSuccessSnackbar(
          `${pluralizeNoun(
            'Document',
            stockDocumentRelatedObjectIds.length
          )} printed`
        );
      } catch (err) {
        dispatchErrorSnackbar('Document printing error');
      }
    },
    [
      dispatchSuccessSnackbar,
      dispatchErrorSnackbar,
      printStockDocumentRelatedObject,
      settings.printer8511,
    ]
  );

  const getPrintingConfiguration = useCallback(
    ({
      recordType,
      labelTemplateName,
    }: {
      recordType: PrintRecordType;
      labelTemplateName?:
        | 'Shipment'
        | 'Stock Location'
        | 'Stock Location Vertical'
        | 'Stock Lot'
        | 'Shipment Line'
        | 'Printer'
        | 'Shipment Bin Tag'
        | 'Customer Bin Tag'
        | 'User'
        | 'Kit Bin Tag'
        | 'Kit Request';
    }): {
      labelTemplate: LabelTemplate | null;
      printer: Printer['id'] | null;
    } => {
      // define defaults and lookups
      const labelTemplatesByRecordType = {
        stockLocation: getLabelTemplate(LabelFormat.StockLocationDefault),
        stockLot: getLabelTemplate(LabelFormat.StockLotDefault),
        printer: getLabelTemplate(LabelFormat.PrinterDefault),
        shipmentLine: getLabelTemplate(LabelFormat.ShipmentLineDefault),
        shipment: getLabelTemplate(LabelFormat.ShipmentDefault),
        user: getLabelTemplate(LabelFormat.UserDefault),
        kitRequest: getLabelTemplate(LabelFormat.KitRequestDefault),
      };

      const labelTemplatesByName = {
        'Stock Location': getLabelTemplate(LabelFormat.StockLocationDefault),
        'Stock Location Vertical': getLabelTemplate(
          LabelFormat.StockLocationVertical
        ),
        'Stock Lot': getLabelTemplate(LabelFormat.StockLotDefault),
        Printer: getLabelTemplate(LabelFormat.PrinterDefault),
        'Shipment Line': getLabelTemplate(LabelFormat.ShipmentLineDefault),
        Shipment: getLabelTemplate(LabelFormat.ShipmentDefault),
        'Shipment Bin Tag': getLabelTemplate(LabelFormat.StockLocationShipment),
        'Customer Bin Tag': getLabelTemplate(LabelFormat.StockLocationCustomer),
        User: getLabelTemplate(LabelFormat.UserDefault),
        'Kit Bin Tag': getLabelTemplate(LabelFormat.StockLocationKit),
        'Kit Request': getLabelTemplate(LabelFormat.KitRequestDefault),
      };

      const printerByPrinterType: { [key: string]: string } = {
        '0201_thermal': settings.printer2212,
        '0302_thermal': settings.printer3020,
        '0402_thermal': settings.printer4020,
        '0406_thermal': settings.printer6040,
        '8511_document': settings.printer8511,
      };

      // select correct label template
      let labelTemplate = labelTemplatesByRecordType[recordType];
      if (labelTemplateName) {
        labelTemplate = labelTemplatesByName[labelTemplateName];
      }
      if (!labelTemplate) {
        dispatchErrorSnackbar('Invalid label template');
        return {
          labelTemplate: null,
          printer: null,
        };
      }

      // select correct printer for label template
      const printer = printerByPrinterType[labelTemplate?.printerType || ''];
      if (!printer) {
        dispatchErrorSnackbar('No printer configured');
        return {
          labelTemplate: null,
          printer: null,
        };
      }

      return {
        labelTemplate,
        printer,
      };
    },
    [
      dispatchErrorSnackbar,
      getLabelTemplate,
      settings.printer2212,
      settings.printer3020,
      settings.printer4020,
      settings.printer6040,
      settings.printer8511,
    ]
  );

  const handleLocalPrintLabelData = useCallback(
    async ({
      recordType,
      labelTemplateName,
      data,
    }: {
      recordType: PrintRecordType;
      labelTemplateName?:
        | 'Shipment'
        | 'Stock Location'
        | 'Stock Location Vertical'
        | 'Stock Lot'
        | 'Shipment Line'
        | 'Printer'
        | 'Shipment Bin Tag'
        | 'Customer Bin Tag'
        | 'Kit Bin Tag'
        | 'Kit Request'
        | 'User';
      data: { [key: string]: any };
    }) => {
      if (!settings.useBrowserPrint) {
        dispatchErrorSnackbar('Browser print is required for this action');
        return;
      }

      const { labelTemplate, printer } = getPrintingConfiguration({
        recordType,
        labelTemplateName,
      });

      if (!labelTemplate || !printer) {
        return;
      }

      try {
        const zpl = generateZpl(
          labelTemplate,
          snakeCase(data) as { [key: string]: any }
        );
        // configure the browser print wrapper
        const zebraBrowserPrint = new ZebraBrowserPrintWrapper();
        if (!Array.isArray(availableZebraPrinters)) {
          dispatchErrorSnackbar('No connected printers');
          return;
        }
        const printerDevice = availableZebraPrinters.find(
          (p) => p.uid === printer
        );
        if (!printerDevice) {
          dispatchErrorSnackbar('Configured printer was not found.');
          return;
        }
        zebraBrowserPrint.setPrinter(printerDevice as Device);
        // print the zpl
        await zebraBrowserPrint.print(zpl);
        dispatchSuccessSnackbar('Label printed');
      } catch (err) {
        dispatchErrorSnackbar('Label printing error');
      }
    },
    [
      dispatchSuccessSnackbar,
      dispatchErrorSnackbar,
      availableZebraPrinters,
      getPrintingConfiguration,
      settings.useBrowserPrint,
    ]
  );

  const handlePrintLabel = useCallback(
    async ({
      recordType,
      recordIdProps,
      labelTemplateName,
      additionalData,
    }: {
      recordType: PrintRecordType;
      recordIdProps: {
        stockLocationId?: string;
        stockLotId?: string;
        printerId?: string;
        shipmentLineId?: string;
        shipmentId?: string;
        userId?: string;
        kitRequestId?: string;
      };
      labelTemplateName?:
        | 'Shipment'
        | 'Stock Location'
        | 'Stock Location Vertical'
        | 'Stock Lot'
        | 'Shipment Line'
        | 'Printer'
        | 'Shipment Bin Tag'
        | 'Customer Bin Tag'
        | 'Kit Bin Tag'
        | 'Kit Request'
        | 'User';
      additionalData?: { [__: string]: unknown };
    }) => {
      const { labelTemplate, printer } = getPrintingConfiguration({
        recordType,
        labelTemplateName,
      });

      if (!labelTemplate || !printer) {
        return;
      }

      const printFunctionByRecordType = {
        stockLocation: printPrintStockLocationLabel,
        stockLot: printPrintStockLotLabel,
        printer: printPrintPrinterLabel,
        shipmentLine: printPrintShipmentLineLabel,
        shipment: printPrintShipmentLabel,
        user: printPrintUserLabel,
        kitRequest: printPrintKitRequestLabel,
      };

      // check that recordIdProps are correct for recordType
      if (recordType === 'stockLocation') {
        if (!recordIdProps.stockLocationId) {
          dispatchErrorSnackbar('Invalid stock location');
          return;
        }
      }
      if (recordType === 'stockLot') {
        if (!recordIdProps.stockLotId) {
          dispatchErrorSnackbar('Invalid stock lot');
          return;
        }
      }
      if (recordType === 'printer') {
        if (!recordIdProps.printerId) {
          dispatchErrorSnackbar('Invalid printer id');
          return;
        }
      }
      if (recordType === 'user') {
        if (!recordIdProps.userId) {
          dispatchErrorSnackbar('Invalid user id');
          return;
        }
      }
      if (recordType === 'shipment') {
        if (!recordIdProps.shipmentId) {
          dispatchErrorSnackbar('Invalid shipment');
          return;
        }
      }
      if (recordType === 'shipmentLine') {
        if (!recordIdProps.shipmentId || !recordIdProps.shipmentLineId) {
          dispatchErrorSnackbar('Invalid shipment line');
          return;
        }
      }
      if (recordType === 'kitRequest') {
        if (!recordIdProps.kitRequestId) {
          dispatchErrorSnackbar('Invalid kit');
          return;
        }
      }

      const payload = {
        printer: !settings.useBrowserPrint ? printer : null,
        labelTemplate: labelTemplate.id,
        additionalData,
        localPrinting: settings.useBrowserPrint,
      } as PrintLabelRequest;

      try {
        // @ts-ignore
        const response = await printFunctionByRecordType[recordType]({
          ...recordIdProps,
          payload,
        }).unwrap();

        if (settings.useBrowserPrint) {
          // configure the browser print wrapper
          const zebraBrowserPrint = new ZebraBrowserPrintWrapper();
          if (!Array.isArray(availableZebraPrinters)) {
            dispatchErrorSnackbar('No connected printers');
            return;
          }
          const printerDevice = availableZebraPrinters.find(
            (p) => p.uid === printer
          );
          if (!printerDevice) {
            dispatchErrorSnackbar('Configured printer was not found.');
            return;
          }
          zebraBrowserPrint.setPrinter(printerDevice as Device);
          // print the zpl
          await zebraBrowserPrint.print(response.zpl || '');
        }
        dispatchSuccessSnackbar('Label printed');
      } catch (err) {
        dispatchErrorSnackbar('Label printing error');
      }
    },
    [
      dispatchSuccessSnackbar,
      dispatchErrorSnackbar,
      availableZebraPrinters,
      getPrintingConfiguration,
      printPrintKitRequestLabel,
      printPrintShipmentLabel,
      printPrintShipmentLineLabel,
      printPrintStockLocationLabel,
      printPrintStockLotLabel,
      printPrintUserLabel,
      printPrintPrinterLabel,
      settings.useBrowserPrint,
    ]
  );

  return {
    handlePrintLabel,
    handlePrintStockDocument,
    handlePrintStockDocumentRelatedObjects,
    getPrintingConfiguration,
    handleLocalPrintLabelData,
    isLoadingConfig: isFetchingLabelTemplates,
    isLoadingPrint:
      isLoadingPrintStockLocationLabel ||
      isLoadingPrintStockLotLabel ||
      isLoadingPrintPrinterLabel ||
      isLoadingPrintShipmentLineLabel ||
      isLoadingPrintShipmentLabel ||
      isLoadingPrintUserLabel ||
      isLoadingPrintStockDocument ||
      isLoadingPrintKitRequestLabel ||
      isLoadingPrintStockDocumentRelatedObject,
  };
}
